import { type FC, memo, useMemo } from "react";
import { cx } from "@stubhub/broadway-foundation/css";
import { Heading, Text } from "@stubhub/broadway-web";
import {
  eventGridListItemCalendarRecipe,
  type EventGridListItemEventState,
  type EventGridListItemKind,
} from "@stubhub/ensemble-discovery";
import { Content } from "src/shared/components/content";
import { playoffCalendarBlockRecipe } from "./PlayoffCalendarBlock.recipe";

interface Props {
  /**
   * Forwarded to the shared calendar recipe so the playoff card mirrors the
   * disabled coloring the regular EGLI calendar uses for sold-out / coming-soon
   * events.
   */
  eventState: EventGridListItemEventState;
  /**
   * Forwarded to the shared calendar recipe so the playoff card mirrors the
   * neutral/brand coloring the regular EGLI calendar uses for sports / non-sports
   * events.
   */
  kind: EventGridListItemKind;
  /**
   * "game"  — away game: label="Game", number=seriesGameNum
   * "home"  — home game: label="Home", number=homeGameNum
   * "tbd"   — date/opponent unknown: centered "TBD", dashed border, no label/number rows
   */
  variant: "game" | "home" | "tbd";
  /** Game number to display. Required when variant is "game" or "home"; ignored for "tbd". */
  gameNum?: number | undefined;
  /**
   * `true` for "if-necessary" games in a playoff series — games that are only
   * played when the series has not already been clinched. Renders an
   * "If necessary" chin under the game number to communicate the contingency.
   */
  isContingent?: boolean | undefined;
}

export const PlayoffCalendarBlock: FC<Props> = memo(({ eventState, kind, variant, gameNum, isContingent }) => {
  const isTbd = variant === "tbd";
  // Outer-shell styling is sourced from the regular calendar block's recipe so
  // both cards stay visually consistent; playoff-specific overrides come from
  // the local recipe.
  const { container, contentContainer, contentPrimary, contentSecondary, chin, chinContent } = useMemo(() => {
    const classes = eventGridListItemCalendarRecipe({
      kind,
      eventState,
    });
    const playoffClasses = playoffCalendarBlockRecipe({
      state: isTbd ? "tbd" : "confirmed",
      eventState,
    });
    return {
      container: cx(classes.container, playoffClasses.container),
      contentContainer: cx(classes.contentContainer, playoffClasses.contentContainer),
      contentPrimary: cx(classes.contentPrimary, playoffClasses.contentPrimary),
      contentSecondary: cx(classes.contentSecondary, playoffClasses.contentSecondary),
      chin: cx(classes.chin, playoffClasses.chin),
      chinContent: cx(classes.chinContent, playoffClasses.chinContent),
    };
  }, [eventState, kind, isTbd]);

  if (isTbd) {
    return (
      <div className={container}>
        <div className={contentContainer}>
          <Heading as="h6" className={contentPrimary} variant="header" size="small" truncate>
            {/* "TBD" */}
            <Content id="cid$#3983126#" />
          </Heading>
        </div>
        {isContingent && (
          <div className={chin}>
            <Text className={chinContent} variant="label" size="extra-small" truncate>
              {/** "If necessary" */}
              <Content id="cid$#4214686#" />
            </Text>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={container}>
      <div className={contentContainer}>
        <Text className={contentSecondary} variant="label" size="extra-small" truncate>
          {variant === "home" ? (
            /** "Home" */ <Content id="cid$#4209696#" />
          ) : (
            /** "Game" */ <Content id="cid$#4209688#" />
          )}
        </Text>
        <Heading as="h6" className={contentPrimary} variant="header" size="small" truncate>
          {gameNum}
        </Heading>
      </div>
      {isContingent && (
        <div className={chin}>
          <Text className={chinContent} variant="label" size="extra-small" truncate>
            {/** "If necessary" */}
            <Content id="cid$#4214686#" />
          </Text>
        </div>
      )}
    </div>
  );
});
PlayoffCalendarBlock.displayName = "PlayoffCalendarBlock";
